import { Injectable, signal, Signal } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AutomataService, Automation } from '@vendasta/automata';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CurrentAutomationService {
  private _automationId = '';
  private automation$$: BehaviorSubject<Automation | null> = new BehaviorSubject<Automation | null>(null);
  /**
   * @deprecated: use automation instead
   */
  public automation$: Observable<Automation | null> = this.automation$$.asObservable();
  public automation: Signal<Automation | null | undefined> = toSignal<Automation | null>(this.automation$);

  get automationId(): string {
    return this._automationId;
  }

  // AutomationRefreshed will signal whenever refreshAutomation is called. The equality is set to false in order to
  // ensure it signals every time, even if the automation id (this signal's value) doesn't change.
  public automationRefreshed = signal<string>('', { equal: (_a, _b) => false });

  constructor(private readonly automataService: AutomataService) {}

  public getCurrentAutomation(): Automation | null {
    return this.automation$$.getValue();
  }

  // This service relies on being provided the automationId from query params, or wherever it is available.
  public init(automationId: string): void {
    if (automationId === this._automationId) {
      return;
    }
    if (this._automationId) {
      this.automation$$.next(null);
    }
    this._automationId = automationId;
    this.refreshAutomation();
  }

  /**
   * Requests that the automation be refreshed.
   */
  public refreshAutomation(): void {
    /**
     * If you get a weird undefined error here:
     * TypeError: You provided 'undefined' where a stream was expected. You can provide an Observable, Promise, ReadableStream, Array, AsyncIterable, or Iterable.
     * it's likely because your step isn't correctly returning its
     * data correctly (i.e., throwing an error). forkJoin makes this hard to debug so go check the display service
     * for whatever your data is.
     */

    this.refreshAutomationAndWait().subscribe();
  }

  /**
   * Requests that the automation be refreshed, emitting the new automation once the operation completes.
   */
  private refreshAutomationAndWait(): Observable<Automation | null> {
    if (!this._automationId) {
      this.automation$$.next(null);
      this.automationRefreshed.set(this._automationId);
      return of(null);
    }
    return this.automataService.getAutomation(this._automationId).pipe(
      take(1),
      map((automation) => {
        this.automation$$.next(automation);
        this.automationRefreshed.set(this._automationId);
        return automation;
      }),
      catchError((err) => {
        this.automation$$.error(err);
        return throwError(err);
      }),
    );
  }
}
